/**
 * Los errores · `BFF_BO.md` §3.5
 *
 * Todos en `application/problem+json` (RFC 9457) y **nunca un 200 con un error
 * dentro**.
 *
 * `title` y `detail` **se enseñan tal cual**. Vienen en el idioma del usuario y
 * son parte del producto, no un volcado técnico: reescribirlos aquí es cambiar el
 * producto desde el cliente, y además los cuatro frontales lo harían distinto.
 */

export interface Problema {
  readonly type?: string;
  readonly title: string;
  readonly status: number;
  readonly detail?: string;
  readonly code?: string;
  readonly trace_id?: string;
  readonly errors?: readonly { field: string; message: string }[];
}

/**
 * Un problema, envuelto en un `Error` de verdad.
 *
 * TanStack Query quiere que lo que se lanza sea un `Error` —y el linter también,
 * con razón: un `throw` de un objeto suelto pierde la traza—. Así el problema
 * viaja intacto hasta la pantalla que lo pinta, y `aProblema` lo desenvuelve.
 */
export class ErrorDeApi extends Error {
  constructor(readonly problema: Problema) {
    super(problema.detail ?? problema.title);
    this.name = 'ErrorDeApi';
  }
}

const GENERICO: Problema = {
  title: 'No se ha podido completar',
  status: 0,
  detail:
    'No hemos podido hablar con el servidor. Comprueba la conexión y vuelve a intentarlo.',
};

/** Normaliza lo que devuelve Eden en `error` a un problema que se pueda pintar. */
export function aProblema(error: unknown): Problema {
  if (error instanceof ErrorDeApi) return error.problema;
  if (!error || typeof error !== 'object') return GENERICO;

  const valor = (error as { value?: unknown; status?: number }).value;
  const estado = (error as { status?: number }).status ?? 0;

  if (valor && typeof valor === 'object' && 'title' in valor) {
    return {
      ...(valor as Problema),
      status: estado || (valor as Problema).status,
    };
  }

  // Un 401 sin cuerpo pasa: el bucle de refresco ya se rindió.
  if (estado === 401) {
    return {
      title: 'Hay que volver a entrar',
      status: 401,
      detail: 'La sesión ha caducado.',
      code: 'unauthorized',
    };
  }

  return { ...GENERICO, status: estado };
}

/**
 * `423 academy_suspended` · §6.5
 *
 * Impago. **Las lecturas siguen funcionando; las escrituras dan 423.** No se
 * apaga la aplicación: un club que se retrasa un mes en pagarnos no puede dejar a
 * 120 familias sin ver el historial de sus hijos.
 */
export const esClubSuspendido = (problema: Problema): boolean =>
  problema.status === 423 || problema.code === 'academy_suspended';

export const esSinPermiso = (problema: Problema): boolean =>
  problema.status === 403;

/**
 * `404`, que **no siempre es un fallo**.
 *
 * Dos sitios del contrato lo usan como respuesta legítima y las dos por la misma
 * razón: el servidor se niega a inventarse un dato.
 *
 * - `GET /clubs/{club}/dpa` responde `404` cuando el club no ha firmado el contrato
 *   de encargo. No hay error: **no hay contrato**.
 * - Las cinco métricas de §A.2 responden `404` para un mes que el proceso de las
 *   02:30 no ha recorrido todavía. «Un cero es una cifra, y una cifra falsa de MRR
 *   es peor que un hueco: se copia a un correo y ya nadie la comprueba.»
 *
 * Quien llama decide qué significa. Lo que no vale es pintar «algo ha fallado»:
 * es la misma familia del cero inventado, solo que al revés.
 */
export const noHayNada = (problema: Problema): boolean =>
  problema.status === 404;
